import { ChevronRight, Home } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/cn';

interface Crumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  crumbs?: Crumb[];
  /** Big H1 page title. Omit to use the compact breadcrumb-as-title header
   *  (the last crumb becomes the page identity and actions sit inline) — used
   *  by list views so a long doctype name lives in the path instead of a
   *  truncated H1 fighting the toolbar for space. */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  status?: React.ReactNode;
  className?: string;
}

/** Renders the leading "home ▸ a ▸ b" breadcrumb trail. */
function Breadcrumb({ crumbs, sizeClass = 'text-caption', boldLast = false }: {
  crumbs: Crumb[];
  sizeClass?: string;
  boldLast?: boolean;
}) {
  return (
    <nav className={cn('flex items-center flex-wrap gap-1.5 min-w-0', sizeClass)} aria-label="Breadcrumb">
      <Link to={'/' as never} className="text-stone hover:text-ink inline-flex items-center">
        <Home className={boldLast ? 'size-4' : 'size-3'} />
      </Link>
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        const isLink = c.to && !last;
        return (
          <span key={i} className="inline-flex items-center gap-1.5 min-w-0">
            <ChevronRight className={cn('text-muted shrink-0', boldLast ? 'size-3.5' : 'size-3')} />
            {isLink
              ? <Link to={c.to as never} className="text-slate hover:text-ink truncate max-w-[220px]">{c.label}</Link>
              : <span className={cn('truncate', last && boldLast ? 'text-ink font-semibold max-w-[360px]' : 'text-ink max-w-[280px]')}>{c.label}</span>}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * Page header — no gradient, no chrome. White canvas, hairline divider below,
 * generous breathing room. Sits flush with the TopChrome above it.
 */
export function PageHeader({ crumbs, title, subtitle, actions, status, className }: PageHeaderProps) {
  // Compact mode — no big H1. The breadcrumb carries the page identity (last
  // crumb bold) and the actions sit inline on the same row, keeping the header
  // to a single tidy line even when the doctype name is long.
  if (title === undefined) {
    return (
      <div className={cn('bg-canvas border-b border-hairline', className)}>
        <div className="px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              {crumbs && crumbs.length > 0 && <Breadcrumb crumbs={crumbs} sizeClass="text-body-sm" boldLast />}
              {status}
            </div>
            {actions && <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">{actions}</div>}
          </div>
          {subtitle && <div className="mt-1.5 text-body-sm text-slate">{subtitle}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-canvas border-b border-hairline', className)}>
      <div className="px-4 sm:px-6 lg:px-8 pt-3 pb-6">
        {crumbs && crumbs.length > 0 && (
          <div className="mb-3">
            <Breadcrumb crumbs={crumbs} />
          </div>
        )}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-[1_1_240px]">
            <div className="flex items-center gap-3 min-w-0">
              {typeof title === 'string'
                ? <h1 className="text-heading-3 text-ink truncate">{title}</h1>
                : title}
              {status}
            </div>
            {subtitle && <div className="mt-1.5 text-body-sm text-slate">{subtitle}</div>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
